'use client';

import { useEffect, useRef } from 'react';
import moment from 'moment';
import usePushNotifications from '@/hooks/usePushNotifications';
import { initializeFirebase } from '@/lib/firebase/config';
import pushNotificationService from '@/lib/firebase/messaging';
import { displayPushNotificationToast } from './PushNotificationToast';

interface PushNotificationProviderProps {
  children: React.ReactNode;
  autoInitialize?: boolean;
}

/**
 * Provider component for Firebase push notifications
 * Initializes Firebase and manages push notification state
 */
export default function PushNotificationProvider({
  children,
  autoInitialize = true,
}: PushNotificationProviderProps) {
  const {
    token,
    isSupported,
    permission,
    isLoading,
    error,
    initialize,
    requestPermission,
  } = usePushNotifications();

  const initializedRef = useRef(false);
  const tokenDisplayedRef = useRef(false);

  // Initialize Firebase on mount and ensure service worker is ready
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const initFirebase = async () => {
      try {
        console.log('[PushNotificationProvider] Initializing Firebase...');
        await initializeFirebase();
        console.log('[PushNotificationProvider] ✅ Firebase initialized');
        
        // Ensure service worker is registered and has config after reload
        if ('serviceWorker' in navigator) {
          console.log('[PushNotificationProvider] Checking service worker status...');
          const registration = await navigator.serviceWorker.ready;
          
          if (registration.active) {
            console.log('[PushNotificationProvider] Service worker is active');
            // Re-inject config to service worker after page reload
            const { getFirebaseConfig } = await import('@/lib/firebase/config');
            const config = await getFirebaseConfig();
            
            console.log('[PushNotificationProvider] Sending Firebase config to service worker...', {
              hasApiKey: !!config.apiKey,
              hasProjectId: !!config.projectId,
            });
            
            registration.active.postMessage({
              type: 'FIREBASE_CONFIG',
              config: config,
            });
            console.log('[PushNotificationProvider] ✅ Service worker config refreshed after page load');
            
            // Also check if service worker is listening
            registration.active.postMessage({
              type: 'PING',
            });
          } else {
            console.warn('[PushNotificationProvider] ⚠️ Service worker is not active');
          }
        } else {
          console.warn('[PushNotificationProvider] ⚠️ Service workers not supported');
        }
      } catch (error) {
        console.error('[PushNotificationProvider] ❌ Failed to initialize Firebase:', error);
      }
    };

    initFirebase();
  }, []);

  // Auto-initialize push notifications if enabled
  useEffect(() => {
    if (!autoInitialize || initializedRef.current) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    // Initialize push notifications function
    const initializePushNotifications = async () => {
      // Check if supported - use direct service check instead of state
      const browserSupported = pushNotificationService.isSupported();
      
      if (!browserSupported) {
        console.warn('[PushNotificationProvider] Push notifications are not supported in this browser');
        // Run debug function to help diagnose
        if (typeof (window as any).debugPushNotifications === 'function') {
          (window as any).debugPushNotifications();
        }
        return;
      }

      // Check current permission status
      const currentPermission = pushNotificationService.getPermissionStatus();
      console.log('[PushNotificationProvider] Current permission status:', currentPermission);

      // Check permission status using current permission from service
      if (currentPermission === 'granted') {
        // Permission already granted, initialize
        console.log('[PushNotificationProvider] Permission already granted, initializing...');
        initialize().catch((error) => {
          console.error('[PushNotificationProvider] Failed to initialize push notifications:', error);
        });
        initializedRef.current = true;
      } else if (currentPermission === 'default') {
        // Permission not yet requested - initialize will request permission
        console.log('[PushNotificationProvider] Requesting notification permission...');
        initialize().catch((error) => {
          console.error('[PushNotificationProvider] Failed to initialize push notifications:', error);
        });
        initializedRef.current = true;
      } else if (currentPermission === 'denied') {
        console.warn('[PushNotificationProvider] Notification permission was denied by user');
        console.warn('[PushNotificationProvider] User must enable notifications in browser settings');
      } else if (currentPermission === null) {
        console.warn('[PushNotificationProvider] Cannot determine permission status');
      }
    };

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      const handleDOMReady = () => {
        if (document.readyState === 'complete') {
          initializePushNotifications();
        }
      };
      document.addEventListener('DOMContentLoaded', handleDOMReady);
      return () => {
        document.removeEventListener('DOMContentLoaded', handleDOMReady);
      };
    }

    // Initialize immediately if DOM is already ready
    initializePushNotifications();
  }, [autoInitialize, initialize]);

  // Display token when available (after permission is granted)
  useEffect(() => {
    if (token && permission === 'granted' && !tokenDisplayedRef.current) {
      tokenDisplayedRef.current = true;
      
      console.log('[PushNotificationProvider] ✅ FCM Token obtained:', token);
      
      // Log full token to console for easy copying
      console.log('[PushNotificationProvider] 📋 Full FCM Token (copy this to send to backend):');
      console.log(token);
      console.log('---');
      
      // You can send this token to your backend to register the device
      // Example: sendTokenToBackend(token);
    }
    
    // Reset display flag if token becomes null (e.g., after refresh)
    if (!token) {
      tokenDisplayedRef.current = false;
    }
  }, [token, permission]);

  // Log errors
  useEffect(() => {
    if (error) {
      console.error('Push notification error:', error);
    }
  }, [error]);

  // Listen for messages from service worker (navigation and logging)
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      console.warn('[PushNotificationProvider] Cannot setup message listener: window or serviceWorker not available');
      return;
    }

    console.log('[PushNotificationProvider] ✅ Setting up service worker message listener...');

    const handleMessage = async (event: Event | MessageEvent) => {
      console.log('[PushNotificationProvider] 📨 Message received from service worker:', event);
      
      // Type guard to check if it's a MessageEvent
      if (!('data' in event) || !event.data) {
        console.warn('[PushNotificationProvider] Message event missing data:', event);
        return;
      }
      
      const messageEvent = event as MessageEvent;
      console.log('[PushNotificationProvider] Message data:', messageEvent.data);

      // Handle navigation to notification URL
      if (messageEvent.data.type === 'NAVIGATE_TO_NOTIFICATION') {
        const url = messageEvent.data.url || '/';
        console.log('[PushNotificationProvider] Navigating to notification URL:', url);
        
        // Use Next.js router if available, otherwise use window.location
        if (typeof window !== 'undefined') {
          window.location.href = url;
        }
      }
      
      // Handle notification logging requests from service worker
      if (messageEvent.data.type === 'LOG_NOTIFICATION') {
        console.log('[PushNotificationProvider] 📬 LOG_NOTIFICATION received from service worker');
        const notification = messageEvent.data.notification;
        
        // Display toast notification using extracted UI component
        await displayPushNotificationToast({
          notification,
        });
        
        // Log the notification
        try {
          const { logNotification } = await import('@/lib/firebase/notificationLogger');
          // Map notification type: 'push' -> 'background', otherwise use the type or default to 'background'
          const notificationType = notification.type === 'push' ? 'background' : (notification.type === 'foreground' ? 'foreground' : 'background');
          await logNotification({
            ...notification,
            notificationType: notificationType as 'foreground' | 'background',
            receivedAt: moment(notification.receivedAt).toDate(),
          });
        } catch (error) {
          console.error('[PushNotificationProvider] Failed to log notification:', error);
        }
      }
      
      // Handle notification click logging
      if (messageEvent.data.type === 'LOG_NOTIFICATION_CLICK') {
        try {
          const { logNotificationClick } = await import('@/lib/firebase/notificationLogger');
          await logNotificationClick(messageEvent.data.notification);
        } catch (error) {
          console.error('[PushNotificationProvider] Failed to log notification click:', error);
        }
      }
    };

    // Listen for messages from service worker
    // Use 'message' event on navigator.serviceWorker (this is the correct way)
    navigator.serviceWorker.addEventListener('message', handleMessage as unknown as EventListener);
    console.log('[PushNotificationProvider] ✅ Added message listener to navigator.serviceWorker');

    // Also set up listener on the controller if it exists
    if (navigator.serviceWorker.controller) {
      console.log('[PushNotificationProvider] Service worker controller exists:', navigator.serviceWorker.controller);
      // The controller is the same as navigator.serviceWorker, so we don't need to add another listener
      // But we can verify it's there
    } else {
      console.warn('[PushNotificationProvider] ⚠️ No service worker controller found. Service worker may not be active yet.');
      
      // Wait for service worker to be ready and set up listener
      navigator.serviceWorker.ready.then((registration) => {
        console.log('[PushNotificationProvider] Service worker is ready:', registration);
        if (navigator.serviceWorker.controller) {
          console.log('[PushNotificationProvider] ✅ Service worker controller is now available');
        }
      });
    }
    
    // Also listen for when service worker becomes available
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[PushNotificationProvider] Service worker controller changed');
      if (navigator.serviceWorker.controller) {
        console.log('[PushNotificationProvider] ✅ New service worker controller available');
      }
    });

    // Process any pending notifications stored when app was closed
    const processPendingNotifications = async () => {
      try {
        const stored = localStorage.getItem('pending_notifications');
        if (stored) {
          const pendingNotifications = JSON.parse(stored);
          const { logNotification } = await import('@/lib/firebase/notificationLogger');
          
          // Log all pending notifications
          for (const notification of pendingNotifications) {
            await logNotification({
              ...notification,
              notificationType: 'background',
              receivedAt: moment(notification.receivedAt).toDate(),
            });
          }
          
          // Clear processed notifications
          localStorage.removeItem('pending_notifications');
          console.log(`[PushNotificationProvider] ✅ Processed ${pendingNotifications.length} pending notifications`);
        }
      } catch (error) {
        console.error('[PushNotificationProvider] Failed to process pending notifications:', error);
      }
    };

    // Process pending notifications after a short delay to ensure auth is ready
    setTimeout(processPendingNotifications, 1000);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage as unknown as EventListener);
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.removeEventListener('message', handleMessage as unknown as EventListener);
      }
    };
  }, []);

  return <>{children}</>;
}

